import React, { useState, useRef } from "react";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Tooltip,
  Title,
  Legend,
} from "chart.js";
import zoomPlugin from "chartjs-plugin-zoom";
import { Bar } from "react-chartjs-2";
import { Activity } from "lucide-react";

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Tooltip,
  Title,
  Legend,
  zoomPlugin
);

const SensorTimelineChart = ({ data }) => {
  const chartRef = useRef();
  const [activeVisible, setActiveVisible] = useState(true);
  const [inactiveVisible, setInactiveVisible] = useState(true);

  if (!Array.isArray(data) || data.length === 0) {
    return (
      <div className="flex items-center justify-center h-64 bg-gray-50 rounded-lg border-2 border-dashed border-gray-300">
        <div className="text-center">
          <Activity className="mx-auto h-12 w-12 text-gray-400 mb-4" />
          <p className="text-gray-500 text-lg">No sensor data available</p>
        </div>
      </div>
    );
  }

  const now = new Date();
  const MS_PER_DAY = 24 * 60 * 60 * 1000;

  const todayMidnight = new Date(now);
  todayMidnight.setHours(0, 0, 0, 0);

  const labelDays = 10; // Show labels for 10 days
  const dataDays = 10; // Show data for last 7 days only

  const labelMidnights = Array.from({ length: labelDays }, (_, i) => {
    const d = new Date(todayMidnight.getTime() - i * MS_PER_DAY);
    return d;
  }).reverse();

  const dayLabels = labelMidnights.map((d) =>
    d.toLocaleDateString("en-CA")
  );

  const allEvents = [];

  data.forEach((asset) => {
    const history = asset.history.L.map((d) => new Date(d.S));
    const isCurrentlyActive = asset.status === true;

    const ranges = [];

    for (let i = 0; i < history.length - 1; i++) {
      const start = history[i];
      const end = history[i + 1];
      const isActive = isCurrentlyActive ? i % 2 === 0 : i % 2 !== 0;
      ranges.push({ start, end, isActive });
    }

    if (history.length > 0) {
      const last = history[history.length - 1];
      ranges.push({ start: last, end: now, isActive: isCurrentlyActive });
    }

    ranges.forEach(({ start, end, isActive }) => {
      for (let i = labelMidnights.length - dataDays; i < labelMidnights.length; i++) {
        const dayStart = labelMidnights[i];
        const dayEnd = new Date(dayStart.getTime() + MS_PER_DAY);

        if (end > dayStart && start < dayEnd) {
          const segmentStart = new Date(Math.max(start.getTime(), dayStart.getTime()));
          const segmentEnd = new Date(Math.min(end.getTime(), dayEnd.getTime()));

          const startOfDay = new Date(segmentStart);
          startOfDay.setHours(0, 0, 0, 0);

          const timeStart = segmentStart.getTime() - startOfDay.getTime();
          const timeEnd = segmentEnd.getTime() - startOfDay.getTime();

          allEvents.push({
            x: [timeStart, timeEnd],
            y: dayLabels[i],
            backgroundColor: isActive
              ? "rgba(239, 68, 68, 0.8)"
              : "rgba(34, 197, 94, 0.8)",
            status: isActive ? "Inactive" : "Active",
          });
        }
      }
    });
  });

  if (allEvents.length === 0) {
    return (
      <div className="flex items-center justify-center h-64 bg-gray-50 rounded-lg border-2 border-dashed border-gray-300">
        <div className="text-center">
          <Activity className="mx-auto h-12 w-12 text-gray-400 mb-4" />
          <p className="text-gray-500 text-lg">No timeline data available</p>
        </div>
      </div>
    );
  }

  const filteredEvents = allEvents.filter(
    (e) =>
      (e.status === "Active" && activeVisible) ||
      (e.status === "Inactive" && inactiveVisible)
  );

  const minHeightPerDate = 20;
  const baseHeight = 4;
  const dynamicHeight = 280;
  console.log({dynamicHeight})

  const chartData = {
    labels: dayLabels,
    datasets: [
      {
        label: "Sensor Activity",
        data: filteredEvents.map((event) => ({
          x: event.x,
          y: event.y,
          status: event.status,
        })),
        backgroundColor: filteredEvents.map((event) => event.backgroundColor),
        borderSkipped: false,
        borderRadius: 8,
        barThickness: Math.max(12, Math.min(2, 800 / dayLabels.length)),
        categoryPercentage: 0.6,
        barPercentage: 0.7,
      },
    ],
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    indexAxis: "y",
    layout: {
      padding: {
        top: 20,
        bottom: 20,
        left: 20,
        right: 20,
      },
    },
    scales: {
      x: {
        type: "linear",
        min: 0,
        max: 24 * 60 * 60 * 1000,
        title: {
          display: true,
          text: "Time of Day",
          font: {
            size: 14,
            weight: 'bold',
          },
          color: '#374151',
        },
        ticks: {
          stepSize: 2 * 60 * 60 * 1000,
          callback: function (value) {
            const hours = Math.floor(value / (60 * 60 * 1000));
            const minutes = Math.floor((value % (60 * 60 * 1000)) / (60 * 1000));
            return `${hours.toString().padStart(2, "0")}:${minutes
              .toString()
              .padStart(2, "0")}`;
          },
          color: '#6B7280',
          font: {
            size: 12,
          },
        },
        grid: {
          color: '#E5E7EB',
          lineWidth: 1,
        },
      },
      y: {
        title: {
          display: true,
          text: "Date",
          font: {
            size: 14,
            weight: 'bold',
          },
          color: '#374151',
        },
        ticks: {
          padding: Math.max(20, 60 - dayLabels.length * 2),
          color: '#374151',
          font: {
            size: Math.max(10, 14 - dayLabels.length * 0.2),
            weight: '500',
          },
          maxRotation: 0,
          minRotation: 0,
        },
        grid: {
          color: '#F3F4F6',
          lineWidth: 2,
        },
        offset: true,
      },
    },
    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        backgroundColor: 'rgba(0, 0, 0, 0.8)',
        titleColor: '#FFFFFF',
        bodyColor: '#FFFFFF',
        borderColor: '#6B7280',
        borderWidth: 1,
        cornerRadius: 6,
        displayColors: false,
        callbacks: {
          label: (context) => {
            const { raw } = context;
            const [start, end] = raw.x;

            const format = (ms) => {
              const hours = Math.floor(ms / (60 * 60 * 1000));
              const minutes = Math.floor((ms % (60 * 60 * 1000)) / (60 * 1000));
              return `${hours.toString().padStart(2, "0")}:${minutes
                .toString()
                .padStart(2, "0")}`;
            };

            return `${raw.status || ""}: ${format(start)} - ${format(end)}`;
          },
        },
      },
    },
  };

  return (
<div className="w-full bg-white shadow-sm p-4">
  <div className="flex justify-end w-full mb-4">
    <h3 className="text-lg text-center font-semibold text-gray-800">Sensor Activity Timeline</h3>
  </div>
  <div className="w-full" style={{ height: `${dynamicHeight}px` }}>
    <Bar ref={chartRef} data={chartData} options={options} />
  </div>
</div>

  );
};

export default SensorTimelineChart;
